import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react'
import { Trash2, RotateCcw, X, RefreshCw, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import { useBin } from './useFiles'
import { useRestoreRunner, restoreFailureText } from './useRestoreRunner'
import { inFlightRestores, JobFailedError, JobLostError, type BinItem, type FilePermissionStr, type RestoreRequestOptions } from '@papan/files/client'
import { formatDate, formatSize } from './formatters'
import { useTranslation } from '@papan/i18n'
import { useToast, useConfirm } from '@papan/core-ui/ui'
import { restoreRefusalMessage } from './restore-refusal'
import { BIN_ITEM_BEING_DELETED_CODE } from '@papan/types'

export interface RestoreTargetPickerProps {
  originalName: string
  onSelect: (folderId: string) => void
  onCancel: () => void
}

export interface BinViewProps {
  /** Resolved `purge_file` capability for the active identity (default admin),
   *  supplied by the caller via useMyCapabilities — gates per-item
   *  permanent-delete instead of a raw role check. */
  canPurge?: boolean
  /** Resolved `empty_bin` capability (default owner-only) — gates the
   *  tenant-wide Empty Bin button, which has no per-item reach check. */
  canEmptyBin?: boolean
  /**
   * Render a folder-picker dialog when the server refuses a restore
   * because the item's original parent is gone (HTTP 409
   * TARGET_PARENT_GONE). Passed as a render prop so BinView doesn't
   * import the workspace package (the picker lives there; importing
   * it here would create a files → workspace cycle on top of the
   * existing workspace → files dependency).
   *
   * When omitted, the 409 surfaces as an error toast with no recovery
   * affordance — fine for tests/headless environments where the picker
   * isn't mounted.
   */
  renderRestoreTargetPicker?: (props: RestoreTargetPickerProps) => ReactNode
  /** #826: open a folder of the active drive (a restore that went into where its folder sits now
   *  shows it). Omitted: the toast names the place only. */
  onOpenFolder?: (folderId: string) => void
}

// The list's fixed columns take ~430px, so a narrow pane (a small window, or
// the drive panel beside another deck) used to leave the NAME column at zero
// width on every row. They drop out as the list itself narrows — least needed
// first — so the name always keeps room. Sized on the list, not the viewport.
const COL_DELETED = 'flex-shrink-0 @max-[720px]/binlist:hidden'
const COL_EXPIRES = 'flex-shrink-0 @max-[600px]/binlist:hidden'
const COL_SIZE = 'flex-shrink-0 @max-[440px]/binlist:hidden'

/** The plan a restore answers with when the item's folder is gone (#826, 409 RESTORE_NEEDS_REBUILD). */
interface RestorePlan {
  recreate: Array<{ name: string; fromBinCopy: boolean }>
  into: string | null
  fingerprint: string
}
type RestoreError = { status?: number; code?: string; message?: string; restorePlan?: RestorePlan; canRelocate?: boolean }

function canWritePerm(p: FilePermissionStr): boolean {
  return p === 'owner' || p === 'admin' || p === 'readwrite'
}

function SidebarRow({
  active,
  onClick,
  label,
  count,
  testId,
  indented = false,
}: {
  active: boolean
  onClick: () => void
  label: string
  count: number | string
  testId: string
  indented?: boolean
}) {
  return (
    <button
      data-testid={testId}
      aria-current={active ? 'true' : undefined}
      onClick={onClick}
      className={`w-full text-left text-sm flex items-center justify-between gap-2 border-l-2 transition-colors py-2 pr-3 ${
        indented ? 'pl-6' : 'pl-3'
      } ${
        active
          ? 'themed-bg-tertiary themed-text border-l-purple-400'
          : 'themed-text-secondary border-l-transparent themed-row-hover'
      }`}
    >
      <span className="truncate">{label}</span>
      <span className="text-xs themed-text-muted flex-shrink-0">{count}</span>
    </button>
  )
}

/**
 * Sidebar row with a leading chevron toggle. The chevron and the
 * label/count area are independent click targets:
 *   - chevron click → toggle expand/collapse (view unchanged)
 *   - label click  → select the view (expand state unchanged)
 * This mirrors the VS Code file tree pattern and keeps both
 * interactions discoverable. When `hasChildren` is false the chevron
 * is hidden (still takes its grid slot so other sibling rows align).
 */
function SidebarRowCollapsible({
  active,
  onClick,
  label,
  count,
  testId,
  expanded,
  onToggleExpanded,
  hasChildren,
}: {
  active: boolean
  onClick: () => void
  label: string
  count: number | string
  testId: string
  expanded: boolean
  onToggleExpanded: () => void
  hasChildren: boolean
}) {
  return (
    <div
      className={`flex items-center text-sm border-l-2 transition-colors ${
        active
          ? 'themed-bg-tertiary themed-text border-l-purple-400'
          : 'themed-text-secondary border-l-transparent themed-row-hover'
      }`}
    >
      <button
        data-testid={`${testId}-toggle`}
        aria-label={expanded ? 'Collapse' : 'Expand'}
        aria-expanded={expanded}
        onClick={onToggleExpanded}
        className={`flex items-center justify-center w-6 h-7 themed-icon hover:themed-icon-active flex-shrink-0 ${
          hasChildren ? '' : 'invisible'
        }`}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      <button
        data-testid={testId}
        aria-current={active ? 'true' : undefined}
        onClick={onClick}
        className="flex-1 text-left flex items-center justify-between gap-2 py-2 pr-3 min-w-0"
      >
        <span className="truncate">{label}</span>
        <span className="text-xs themed-text-muted flex-shrink-0">{count}</span>
      </button>
    </div>
  )
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="p-8 text-center themed-text-muted">
      <Trash2 size={48} className="mx-auto mb-4 opacity-50" />
      <p>{text}</p>
    </div>
  )
}

/** #894: a section count — "N+" when the server's total is a lower bound. */
function formatBinCount(total: number, lowerBound: boolean): string {
  return lowerBound ? `${total}+` : String(total)
}

/**
 * #894: the end of a paged Bin list. Loads the next page when it scrolls into
 * view (with room to spare), and is a button too — for keyboards, and for a
 * page that didn't fill the pane.
 */
function LoadMoreRow({ hasMore, loading, onLoad, testId }: { hasMore: boolean; loading: boolean; onLoad: () => void; testId: string }) {
  const { t } = useTranslation('files')
  const ref = useRef<HTMLDivElement>(null)
  const onLoadRef = useRef(onLoad)
  onLoadRef.current = onLoad
  // Observed afresh after every load: a row still in view once a page arrived
  // (a short page) fires again on observe and asks for the next one.
  useEffect(() => {
    const el = ref.current
    if (!el || !hasMore || loading || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) onLoadRef.current()
    }, { rootMargin: '400px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [hasMore, loading])
  if (!hasMore) return null
  return (
    <div ref={ref} className="px-4 py-3 flex justify-center" data-testid={testId}>
      <button
        type="button"
        onClick={onLoad}
        disabled={loading}
        className="flex items-center gap-2 px-3 py-1.5 text-sm themed-text-muted themed-row-hover rounded-md transition-colors disabled:opacity-60"
      >
        {loading && <Loader2 size={14} className="animate-spin" />}
        {loading ? t('binView.status.loadingMore') : t('binView.actions.loadMore')}
      </button>
    </div>
  )
}

function SectionGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="px-4 py-2 text-xs themed-text-muted uppercase tracking-wide themed-bg-secondary border-b themed-border">
        {title}
      </div>
      {children}
    </div>
  )
}

export function BinView({ canPurge, canEmptyBin, renderRestoreTargetPicker, onOpenFolder }: BinViewProps) {
  // Per-item permanent delete gates on the resolved `purge_file` capability
  // (default admin) — server is source of truth via canPurge. The per-item
  // check below additionally requires admin/owner access on the row.
  const canHardDelete = canPurge ?? false
  // The tenant-wide Empty Bin button gates on `empty_bin` (default owner-only) —
  // no per-item reach, so it's a stricter, separate capability.
  const canEmpty = canEmptyBin ?? false
  const { t } = useTranslation('files')
  const toast = useToast()
  const confirm = useConfirm()
  const {
    myBin,
    myBinSection,
    sharedBinByTenant,
    loading,
    refresh,
    loadMore,
    deletePermanently,
    emptyBin,
    emptying,
  } = useBin()
  // #854: a big restore runs as a background job with its own progress toast.
  const { restore: runRestore } = useRestoreRunner()
  // Rows this tab has a Restore request out for (the server's marker covers the
  // rest once the listing refreshes, in every window).
  const inFlight = useSyncExternalStore(inFlightRestores.subscribe, inFlightRestores.get)

  // Open when a restore call hits 409 TARGET_PARENT_GONE — holds the
  // bin id + display name so the picker prompt is human-readable and
  // the retry can fire against the right row.
  const [pickerFor, setPickerFor] = useState<{ binId: string; originalName: string } | null>(null)

  // Unified sidebar selection. Values: 'mine' (just myBin),
  // 'shared' (every shared org grouped under sub-headers), or a tenantId
  // (just that org's items). Replaces the old top-tab structure —
  // everything lives in the left rail now.
  const [activeView, setActiveView] = useState<string>('mine')

  // Shared Bin parent row is collapsible — when collapsed, per-org child
  // rows are hidden. Auto-expands whenever a child org is selected so the
  // active state stays visible.
  const [sharedExpanded, setSharedExpanded] = useState(true)

  // Stable, name-ordered list of (tenantId, name, items) for the
  // Shared Bin section. useMemo so the alphabetical order doesn't
  // flicker on every render.
  const sharedSections = useMemo(() => {
    return Object.entries(sharedBinByTenant)
      .map(([tenantId, group]) => ({
        tenantId,
        name: group.name,
        items: group.items,
        total: group.total,
        totalIsLowerBound: group.totalIsLowerBound,
        hasMore: group.nextCursor !== null,
        loadingMore: group.loadingMore,
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [sharedBinByTenant])

  // #894: the Bin is paged — counts come from the server's totals, never from
  // how many rows are loaded. A non-owner's total can be a lower bound ("N+").
  const sharedCount = useMemo(
    () => sharedSections.reduce((sum, s) => sum + s.total, 0),
    [sharedSections],
  )
  const sharedLowerBound = sharedSections.some((s) => s.totalIsLowerBound)
  const totalCount = myBinSection.total + sharedCount
  const totalLowerBound = myBinSection.totalIsLowerBound || sharedLowerBound

  // If the active view is a tenantId that disappeared (last item
  // restored / dismissed / etc.) snap back to the Shared Bin group view.
  // The fixed views ('mine' / 'shared') never need fixup. Also auto-
  // expand the Shared Bin parent whenever a child org is selected, so
  // the active state stays visible in the sidebar.
  useEffect(() => {
    const isFixed = activeView === 'mine' || activeView === 'shared'
    if (isFixed) return
    const stillExists = sharedSections.some((s) => s.tenantId === activeView)
    if (!stillExists) {
      setActiveView('shared')
    } else if (!sharedExpanded) {
      setSharedExpanded(true)
    }
  }, [sharedSections, activeView, sharedExpanded])

  const activeTenantSection = useMemo(
    () => sharedSections.find((s) => s.tenantId === activeView) ?? null,
    [sharedSections, activeView],
  )

  /** Restore, then refetch the Bin. A job's progress toast has already spoken;
   *  an inline restore gets the usual "restored" toast here — naming the folders
   *  it brought back when its own folder was gone (#826). */
  const restore = async (binId: string, originalName: string, targetParentId?: string | null, opts?: RestoreRequestOptions) => {
    const run = await runRestore({ binId, name: originalName }, targetParentId, opts)
    refresh()
    if (run.kind !== 'done') return
    // #826 round 9: restored into where its folder sits now (a manager moved it) — say where, and
    // open that folder when the Bin can.
    if (run.restoredInto) {
      const { folderId, path } = run.restoredInto
      if (folderId && path && onOpenFolder) {
        onOpenFolder(folderId)
        toast.success(t('binView.toast.restoredIntoOpened', { name: originalName, path }))
      } else if (path) {
        toast.success(t('binView.toast.restoredInto', { name: originalName, path }))
      } else {
        toast.success(t('binView.toast.restoredIntoLive', { name: originalName }))
      }
      return
    }
    const folders = run.recreatedFolders?.map(f => `"${f}"`).join(', ')
    // #826 R4: a name taken where it went back is never a silent "(1)".
    if (run.renamedTo && folders) toast.success(t('binView.toast.restoredRebuiltAs', { name: originalName, newName: run.renamedTo, folders }))
    else if (run.renamedTo) toast.success(t('binView.toast.restoredAs', { name: originalName, newName: run.renamedTo }))
    else if (folders) toast.success(t('binView.toast.restoredRebuilt', { name: originalName, folders }))
    else toast.success(t('binView.toast.restored'))
  }

  /** The toast for a refusal: #854's first (its words for "uploads paused" name the
   *  restore), then the ones the server explains (#801 E4, #826's rebuild), else generic. */
  const refusalToast = (err: unknown, name: string) =>
    toast.error(restoreFailureText(err, name, t) ?? restoreRefusalMessage(t, err, name) ?? t('binView.toast.restoreFailed'))

  /** Restore; when the server asks to confirm an access change (the guard a drive move
   *  runs — a different folder, or a new folder in the gone one's place), ask, then
   *  restore with the confirmation. */
  const restoreConfirmingAccess = async (binId: string, name: string, targetParentId: string | undefined, opts: RestoreRequestOptions = {}) => {
    try {
      await restore(binId, name, targetParentId, opts)
    } catch (err) {
      const e = err as RestoreError
      // #1119: a folder chosen at the top of the drive where its name is reserved (or a rebuild that would
      // land one there, for someone who may relocate) — say why and offer the picker again.
      if (e?.code === 'RESERVED_ROOT' && renderRestoreTargetPicker && (targetParentId !== undefined || e.canRelocate)) {
        toast.info(restoreRefusalMessage(t, err, name, { pickerOpens: true }) ?? t('binView.toast.restoreNeedsTarget', { name }))
        setPickerFor({ binId, originalName: name })
        return
      }
      // #826: the confirmed rebuild was refused (R2 without Manage on both folders, or the rebuild
      // itself — re-judged at the act) but this person may put it elsewhere: the picker.
      const pickerCodes = ['ACCESS_CHANGE_FORBIDDEN', 'RESTORE_PATH_REFUSED', 'RESTORE_REBUILD_UNAVAILABLE']
      if (pickerCodes.includes(e?.code ?? '') && e.canRelocate && renderRestoreTargetPicker && opts.rebuildPath) {
        toast.info(restoreRefusalMessage(t, err, name, { pickerOpens: true }) ?? t('binView.toast.restoreNeedsTarget', { name }))
        setPickerFor({ binId, originalName: name })
        return
      }
      if (e?.status !== 409 || e?.code !== 'ACCESS_CHANGE_NEEDS_CONFIRMATION') return refusalToast(err, name)
      const ok = await confirm({
        title: t('binView.confirm.accessChangeTitle'),
        message: t('binView.confirm.accessChange', { name }),
        confirmText: t('binView.actions.restoreAnyway'),
      })
      if (!ok) return
      try {
        await restore(binId, name, targetParentId, { ...opts, confirmedAccessChange: true })
      } catch (err2) {
        refusalToast(err2, name)
      }
    }
  }

  /** #826: the item's folder is gone. Show what the restore brings back (or the new
   *  folder in its place it goes into) and restore there on confirm. Someone who may put
   *  it elsewhere gets "Choose another folder" as the second button instead of Cancel. */
  const confirmRebuild = async (binId: string, name: string, plan: RestorePlan, canRelocate: boolean) => {
    const canPick = canRelocate && !!renderRestoreTargetPicker
    const folderLine = (l: RestorePlan['recreate'][number]) =>
      t(l.fromBinCopy ? 'binView.confirm.rebuildFolderFromBin' : 'binView.confirm.rebuildFolderCopied', { folder: l.name })
    const ok = await confirm({
      title: t('binView.confirm.rebuildTitle', { name }),
      type: 'info',
      message: (
        <div className="space-y-2" data-testid="bin-rebuild-plan">
          <p>{plan.into ? t('binView.confirm.rebuildInto', { name, folder: plan.into }) : t('binView.confirm.rebuildIntro', { name })}</p>
          {plan.recreate.length > 0 && (
            <>
              {plan.into && <p>{t('binView.confirm.rebuildIntoThenFolders')}</p>}
              <ul className="list-disc pl-5">
                {plan.recreate.map((l, i) => <li key={i}>{folderLine(l)}</li>)}
              </ul>
            </>
          )}
        </div>
      ),
      confirmText: t('binView.actions.restore'),
      ...(canPick ? { cancelText: t('binView.actions.chooseAnotherFolder') } : {}),
    })
    if (!ok) {
      if (canPick) setPickerFor({ binId, originalName: name })
      return
    }
    await restoreConfirmingAccess(binId, name, undefined, { rebuildPath: true, planFingerprint: plan.fingerprint })
  }

  const handleRestore = async (binId: string, originalName: string) => {
    try {
      await restore(binId, originalName)
    } catch (err) {
      const e = err as RestoreError
      // #826: the original folder is gone — the server sends the plan to rebuild it.
      if (e?.status === 409 && e?.code === 'RESTORE_NEEDS_REBUILD' && e.restorePlan) {
        await confirmRebuild(binId, originalName, e.restorePlan, !!e.canRelocate)
        return
      }
      // The rebuild can't happen (refused, or an app to restore first), but this person may put
      // the item somewhere else (the owner, or an item with no access of its own): say why, and
      // offer the folder picker rather than a dead end.
      // #1119: a rebuild that would land a reserved top-level folder (400 RESERVED_ROOT) — the same.
      if ((e?.code === 'RESTORE_PATH_REFUSED' || e?.code === 'RESTORE_REBUILD_UNAVAILABLE' || e?.code === 'RESERVED_ROOT') && e.canRelocate && renderRestoreTargetPicker) {
        toast.info(restoreRefusalMessage(t, err, originalName, { pickerOpens: true }) ?? t('binView.toast.restoreNeedsTarget', { name: originalName }))
        setPickerFor({ binId, originalName })
        return
      }
      // An older server: 409 TARGET_PARENT_GONE with no plan. Opening the picker lets
      // the user choose a safe target instead of silently relocating to the drive root
      // (which would leak Restricted-folder contents into the public root).
      if (e?.status === 409 && e?.message === 'TARGET_PARENT_GONE') {
        toast.info(t('binView.toast.restoreNeedsTarget', { name: originalName }))
        setPickerFor({ binId, originalName })
        return
      }
      refusalToast(err, originalName)
    }
  }

  const handlePickerSelect = async (folderId: string) => {
    if (!pickerFor) return
    const target = pickerFor
    setPickerFor(null)
    // Restoring into a NEW folder whose access differs from the original: the server
    // asks for an explicit confirmation (the same guard a move runs).
    await restoreConfirmingAccess(target.binId, target.originalName, folderId)
  }

  const handleDelete = async (binId: string, name: string) => {
    const confirmed = await confirm({
      title: t('binView.confirm.deleteTitle'),
      message: t('binView.confirm.deletePermanently'),
      type: 'danger',
      confirmText: t('binView.actions.deletePermanently'),
    })
    if (!confirmed) return
    // #1167 S2: a big entry runs as a background job — its toast follows it to the end.
    const job: { progress: ReturnType<typeof toast.progress> | null } = { progress: null }
    try {
      await deletePermanently(binId, {
        onQueued: () => { job.progress = toast.progress(t('binView.toast.deletingForever', { name }), 0) },
      })
      if (job.progress) job.progress.complete(t('binView.toast.deletedPermanently'))
      else toast.success(t('binView.toast.deletedPermanently'))
    } catch (err) {
      const code = (err as { code?: string } | null)?.code
      const text = code === BIN_ITEM_BEING_DELETED_CODE
        ? t('binView.toast.beingDeleted', { name })
        : err instanceof JobLostError
          ? t('binView.toast.deleteOutcomeUnknown', { name })
          : (err instanceof JobFailedError && err.message) ? err.message : t('binView.toast.deleteFailed')
      if (job.progress) job.progress.fail(text)
      else toast.error(text)
    }
  }

  const handleEmptyBin = async () => {
    const confirmed = await confirm({
      title: t('binView.confirm.emptyTitle'),
      message: t('binView.confirm.emptyBin'),
      type: 'danger',
      confirmText: t('binView.actions.emptyBin'),
    })
    if (confirmed) {
      // #969: the purge runs as a background job; the bar follows its batches.
      const progressCtrl = toast.progress(t('binView.toast.emptying'), myBinSection.total)
      try {
        const deleted = await emptyBin((done, total) => progressCtrl.update(
          done, t('binView.toast.emptyingCount', { done: done.toLocaleString(), total: total.toLocaleString() }), total))
        progressCtrl.complete(t('binView.toast.emptied', { count: deleted }))
      } catch (err) {
        progressCtrl.fail(err instanceof JobLostError
          ? t('binView.toast.emptyOutcomeUnknown')
          : (err instanceof JobFailedError && err.message) ? err.message : t('binView.toast.emptyFailed'))
      }
    }
  }

  // Per-item row. Restore button is disabled (greyed) when the caller
  // can see the item (canRead) but lacks canWrite at the source —
  // matches the plan's "visible but unrestorable" rule. The hard-delete
  // button stays gated on canHardDelete (the purge_file capability, default
  // admin+) so it never shows up on Shared Bin items for non-owner recipients.
  const renderRow = (item: BinItem, sourceTenantName: string) => {
    const itemCanWrite = canWritePerm(item.effectivePermission)
    // #854: being restored (a job, in any window, or this tab's request) — no
    // actions until it lands in the drive or fails.
    const restoring = !!item.restoring || inFlight.has(item.id)
    return (
      <div
        key={item.id}
        data-testid={`bin-row-${item.id}`}
        aria-busy={restoring || undefined}
        className="h-12 flex items-center px-4 border-b themed-row-border themed-row-hover transition-colors group"
      >
        {/* The name is a DIRECT child of the row (e2e reaches the row as the
            name's parent); the "Restoring…" label and a spacer are its siblings. */}
        <span className={`min-w-0 themed-text truncate ${restoring ? 'opacity-60' : ''}`}>{item.originalName}</span>
        {restoring && (
          <span className="ml-2 flex items-center gap-1 text-xs themed-text-muted flex-shrink-0" data-testid="bin-row-restoring">
            <Loader2 size={12} className="animate-spin" />
            {t('binView.status.restoring')}
          </span>
        )}
        <div className="flex-1" />
        <div className={`w-40 text-sm themed-text-muted ${COL_DELETED}`}>{formatDate(item.deletedAt)}</div>
        <div className={`w-24 text-sm themed-text-muted ${COL_SIZE}`}>{formatSize(item.totalSize)}</div>
        <div className={`w-24 text-sm themed-text-muted ${COL_EXPIRES}`}>{formatDate(item.expiresAt)}</div>
        <div className="w-20 flex-shrink-0 flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {!restoring && <>
            <button
              onClick={() => itemCanWrite && handleRestore(item.id, item.originalName)}
              disabled={!itemCanWrite}
              className={`p-1.5 themed-icon rounded ${
                itemCanWrite
                  ? 'hover:text-green-400 hover:themed-bg-tertiary'
                  : 'opacity-40 cursor-not-allowed'
              }`}
              title={
                itemCanWrite
                  ? t('binView.actions.restore')
                  : t('binView.section.noWriteAccess', { name: sourceTenantName })
              }
            >
              <RotateCcw size={16} />
            </button>
            {canHardDelete && (item.effectivePermission === 'admin' || item.effectivePermission === 'owner') && (
              <button
                onClick={() => handleDelete(item.id, item.originalName)}
                className="p-1.5 themed-icon hover:text-red-400 hover:themed-bg-tertiary rounded"
                title={t('binView.actions.deletePermanently')}
              >
                <X size={16} />
              </button>
            )}
          </>}
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 min-h-0 themed-bg-primary flex flex-col">
      {/* Header */}
      <div className="h-12 border-b themed-border flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <Trash2 size={20} className="themed-icon" />
          <span className="themed-text font-medium">{t('binView.title')}</span>
          <span className="themed-text-muted text-sm">{t(totalLowerBound ? 'binView.itemCountAtLeast' : 'binView.itemCount', { count: totalCount })}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            className="p-2 themed-icon hover:themed-icon-active themed-row-hover rounded-md transition-colors"
            title={t('common:refresh')}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          {canEmpty && activeView === 'mine' && myBinSection.total > 0 && !emptying && (
            <button
              onClick={handleEmptyBin}
              className="flex items-center gap-2 px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 text-red-400 text-sm rounded-md transition-colors"
            >
              <Trash2 size={16} />
              {t('binView.actions.emptyBin')}
            </button>
          )}
        </div>
      </div>

      {/* #969: an Empty Bin running — started in another window or before a
          reload (this tab's own one shows in its progress toast). */}
      {emptying && !emptying.owned && (
        <div className="px-4 py-2 border-b themed-border themed-bg-secondary text-sm themed-text-secondary flex items-center gap-3" data-testid="bin-emptying" role="status">
          <Loader2 size={14} className="animate-spin flex-shrink-0" />
          <span className="min-w-0 truncate">
            {t('binView.status.emptyingCount', { done: emptying.done.toLocaleString(), total: emptying.total.toLocaleString() })}
          </span>
          {emptying.total > 0 && (
            <div className="ml-auto h-1.5 w-32 flex-shrink-0 rounded-full themed-bg-tertiary overflow-hidden">
              <div className="h-full bg-red-500/70 transition-[width]" style={{ width: `${Math.min(100, Math.round((emptying.done / emptying.total) * 100))}%` }} />
            </div>
          )}
        </div>
      )}

      {/* Info banner */}
      <div className="px-4 py-3 border-b themed-border themed-bg-secondary text-sm themed-text-secondary">
        {t('binView.info.autoDelete')}
      </div>

      {/* Body — unified sidebar + right pane. The sidebar lists every
          navigation target (All / My Bin / Shared Bin / per-org); the
          right pane renders what's selected. Replaces the earlier
          top-tab structure so users with many source orgs don't have
          to scroll past one org to reach the next. */}
      <div className="flex-1 min-h-0 flex">
        {/* Sidebar */}
        <aside
          className="w-56 border-r themed-border flex flex-col flex-shrink-0"
          aria-label={t('binView.title')}
        >
          <div className="flex-1 overflow-auto py-1">
            <SidebarRow
              testId="bin-view-mine"
              active={activeView === 'mine'}
              onClick={() => setActiveView('mine')}
              label={t('binView.section.myBin')}
              count={formatBinCount(myBinSection.total, myBinSection.totalIsLowerBound)}
            />
            <SidebarRowCollapsible
              testId="bin-view-shared"
              active={activeView === 'shared'}
              onClick={() => setActiveView('shared')}
              label={t('binView.section.sharedBin')}
              count={formatBinCount(sharedCount, sharedLowerBound)}
              expanded={sharedExpanded}
              onToggleExpanded={() => setSharedExpanded((v) => !v)}
              hasChildren={sharedSections.length > 0}
            />
            {sharedExpanded && sharedSections.map((section) => (
              <SidebarRow
                key={section.tenantId}
                testId={`bin-shared-source-${section.tenantId}`}
                active={activeView === section.tenantId}
                onClick={() => setActiveView(section.tenantId)}
                label={section.name}
                count={formatBinCount(section.total, section.totalIsLowerBound)}
                indented
              />
            ))}
          </div>
        </aside>

        {/* Right pane — content depends on activeView */}
        <div className="@container/binlist flex-1 min-w-0 flex flex-col">
          {loading && totalCount === 0 ? (
            <div className="p-4 themed-text-muted text-sm">{t('binView.status.loading')}</div>
          ) : (
            <>
              <div className="h-10 border-b themed-border flex items-center px-4 text-xs themed-text-muted uppercase tracking-wide flex-shrink-0">
                <div className="flex-1 min-w-0 truncate">{t('binView.header.originalLocation')}</div>
                <div className={`w-40 ${COL_DELETED}`}>{t('binView.header.deleted')}</div>
                <div className={`w-24 ${COL_SIZE}`}>{t('binView.header.size')}</div>
                <div className={`w-24 ${COL_EXPIRES}`}>{t('binView.header.expires')}</div>
                <div className="w-20 flex-shrink-0"></div>
              </div>
              <div className="flex-1 overflow-auto">
                {activeView === 'mine' ? (
                  myBin.length === 0 ? (
                    <EmptyState text={t('binView.status.myBinEmpty')} />
                  ) : (
                    <>
                      {myBin.map((item) => renderRow(item, t('binView.section.myBin')))}
                      <LoadMoreRow
                        testId="bin-load-more-mine"
                        hasMore={myBinSection.nextCursor !== null}
                        loading={myBinSection.loadingMore}
                        onLoad={() => loadMore('mine')}
                      />
                    </>
                  )
                ) : activeView === 'shared' ? (
                  sharedSections.length === 0 ? (
                    <EmptyState text={t('binView.status.sharedBinEmpty')} />
                  ) : (
                    sharedSections.map((section) => (
                      <SectionGroup
                        key={section.tenantId}
                        title={t('binView.section.sharedBinFromTenant', { name: section.name, count: section.total })}
                      >
                        {section.items.map((item) => renderRow(item, section.name))}
                        <LoadMoreRow
                          testId={`bin-load-more-${section.tenantId}`}
                          hasMore={section.hasMore}
                          loading={section.loadingMore}
                          onLoad={() => loadMore(section.tenantId)}
                        />
                      </SectionGroup>
                    ))
                  )
                ) : activeTenantSection ? (
                  <>
                    {activeTenantSection.items.map((item) => renderRow(item, activeTenantSection.name))}
                    <LoadMoreRow
                      testId={`bin-load-more-${activeTenantSection.tenantId}`}
                      hasMore={activeTenantSection.hasMore}
                      loading={activeTenantSection.loadingMore}
                      onLoad={() => loadMore(activeTenantSection.tenantId)}
                    />
                  </>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Restore-target picker (rendered by the app — see prop docs) */}
      {pickerFor && renderRestoreTargetPicker?.({
        originalName: pickerFor.originalName,
        onSelect: handlePickerSelect,
        onCancel: () => setPickerFor(null),
      })}
    </div>
  )
}
